// src/components/MilestoneWall.js
//
// The shared feed. Each row is one member's milestone, with a tap-to-cheer and
// a flat comment thread.
//
// Comment threads are LAZY — a row fetches its comments only when opened, so a
// wall of 20 rows costs one query, not twenty-one.

import React, { useState, useEffect, useCallback } from 'react';
import MilestoneBadge from './MilestoneBadge';
import MilestoneDetail, { badgeButtonReset } from './MilestoneDetail';
import { BADGE_BY_KEY } from '../services/milestones';
import AvatarPhoto from './AvatarPhoto';
import MemberCardTrigger from './MemberCardTrigger';
import { useMemberPhotos } from '../services/memberProfiles';
import {
    fetchWall, fetchMyCheers, toggleCheer,
    fetchMilestoneComments, postMilestoneComment, deleteMilestoneComment,
} from '../services/milestoneWall';
import { notifyMilestoneComment } from '../services/commentNotifications';

// Page size for both the initial load and each "Show more". Pages are fetched
// on demand rather than pre-loaded, so the Wall can be walked all the way back
// through history without ever loading it all at once.
const PAGE = 10;

// How many comments render when a thread is opened. Threads show the MOST
// RECENT few — on a congratulations thread the latest messages are the live
// conversation, and the earliest are the ones you've likely already seen.
// "Show earlier" walks backwards from there.
const COMMENTS_VISIBLE = 5;
const COMMENTS_STEP = 10;

function relativeTime(iso) {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const mins = Math.floor((Date.now() - then) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days} days ago`;
    return new Date(iso).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
        ...(new Date(iso).getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    });
}

function initialsFor(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
}

// Module-scope, never nested — a component defined inside another remounts on
// every keystroke and drops input focus.
function CheerButton({ on, count, onClick, disabled }) {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: on
                    ? 'color-mix(in srgb, var(--mom-primary) 12%, transparent)'
                    : (hovered ? 'var(--mom-cream-dark)' : 'var(--mom-cream)'),
                color: on ? 'var(--mom-primary)' : 'var(--mom-charcoal-light, #5c5651)',
                border: 'none', borderRadius: 99, padding: '7px 15px',
                fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, fontWeight: 600,
                cursor: disabled ? 'default' : 'pointer',
                transition: 'background 0.15s ease, color 0.15s ease',
            }}
        >
            <svg width="14" height="14" viewBox="0 0 24 24" fill={on ? 'currentColor' : 'none'}
                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.6 1.1-1a5.5 5.5 0 0 0 0-7.8z" />
            </svg>
            {count > 0 ? count : 'Cheer'}
        </button>
    );
}

function CommentRow({ c, canDelete, onDelete, photoUrl }) {
    return (
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
            <MemberCardTrigger userId={c.isCoach ? null : c.userId} name={c.displayName || 'Member'} display="block">
            <div style={{
                width: 26, height: 26, borderRadius: '50%', flex: '0 0 auto', position: 'relative',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--mom-font-sans)', fontSize: 10.5, fontWeight: 700, color: '#fff',
                background: c.isCoach
                    ? 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))'
                    : 'linear-gradient(135deg, #b6a08f, #9c8878)',
            }}>
                {c.isCoach ? 'D' : initialsFor(c.displayName)}
                {!c.isCoach && <AvatarPhoto url={photoUrl} />}
            </div>
            </MemberCardTrigger>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 13, lineHeight: 1.55,
                    color: 'var(--mom-charcoal-light, #4a4540)', wordBreak: 'break-word',
                }}>
                    <b style={{ color: 'var(--mom-charcoal)', fontWeight: 700 }}>
                        {c.isCoach ? 'Danielle' : c.displayName}
                    </b>{' '}
                    {c.text}
                </div>
                {canDelete && (
                    <button
                        onClick={onDelete}
                        style={{
                            background: 'none', border: 'none', padding: '3px 0', cursor: 'pointer',
                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)',
                        }}
                    >
                        Delete
                    </button>
                )}
            </div>
        </div>
    );
}

function MilestoneWall({ userId, displayName, isAdmin, emptyLabel, getToken, viewerMilestones = null, copy = {} }) {
    const [rows, setRows] = useState([]);
    // Badge detail card for a Wall post: { badgeKey, name, earnedAt }. Shows how
    // that badge is earned + the viewer's own progress (viewerMilestones).
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cheered, setCheered] = useState(new Set());
    const [openThread, setOpenThread] = useState(null);
    const [threads, setThreads] = useState({});
    // Photos looked up by userId (adr-member-profile-photos); wall docs unchanged.
    const photoFor = useMemberPhotos([
        ...rows.map((r) => r.userId),
        ...Object.values(threads).flat().map((cm) => cm && cm.userId),
    ]);
    const [drafts, setDrafts] = useState({});
    const [posting, setPosting] = useState(false);
    const [commentShown, setCommentShown] = useState({});
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const { rows: page, lastDoc, hasMore: more } = await fetchWall(PAGE);
            if (cancelled) return;
            setRows(page);
            setCursor(lastDoc);
            setHasMore(more);
            setLoading(false);
            if (userId && page.length) {
                const mine = await fetchMyCheers(userId, page.map((r) => r.id));
                if (!cancelled) setCheered(mine);
            }
        })();
        return () => { cancelled = true; };
    }, [userId]);

    // Fetches the NEXT page from Firestore rather than revealing pre-loaded rows,
    // so there is no ceiling on how far back a member can go.
    const loadMore = useCallback(async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        const { rows: page, lastDoc, hasMore: more } = await fetchWall(PAGE, cursor);
        setRows((prev) => {
            // Guard against a duplicate id if a milestone were written between pages.
            const seen = new Set(prev.map((r) => r.id));
            return [...prev, ...page.filter((r) => !seen.has(r.id))];
        });
        setCursor(lastDoc);
        setHasMore(more);
        setLoadingMore(false);
        if (userId && page.length) {
            const mine = await fetchMyCheers(userId, page.map((r) => r.id));
            setCheered((prev) => new Set([...prev, ...mine]));
        }
    }, [loadingMore, hasMore, cursor, userId]);

    const onCheer = useCallback(async (row) => {
        if (!userId) return;
        const on = !cheered.has(row.id);
        // Optimistic — a cheer should feel instant and costs nothing if it fails.
        setCheered((prev) => {
            const next = new Set(prev);
            if (on) next.add(row.id); else next.delete(row.id);
            return next;
        });
        setRows((prev) => prev.map((r) => (
            r.id === row.id ? { ...r, cheerCount: Math.max(0, (r.cheerCount || 0) + (on ? 1 : -1)) } : r
        )));
        const ok = await toggleCheer(userId, row.id, on);
        if (!ok) {
            setCheered((prev) => {
                const next = new Set(prev);
                if (on) next.delete(row.id); else next.add(row.id);
                return next;
            });
            setRows((prev) => prev.map((r) => (
                r.id === row.id ? { ...r, cheerCount: Math.max(0, (r.cheerCount || 0) + (on ? -1 : 1)) } : r
            )));
        }
    }, [userId, cheered]);

    const openComments = useCallback(async (row) => {
        if (openThread === row.id) {
            setOpenThread(null);
            // Reopening starts at the most recent few again rather than wherever
            // the member had expanded to.
            setCommentShown((prev) => ({ ...prev, [row.id]: COMMENTS_VISIBLE }));
            return;
        }
        setOpenThread(row.id);
        if (threads[row.id]) return; // cached for this mount
        const list = await fetchMilestoneComments(row.id);
        setThreads((prev) => ({ ...prev, [row.id]: list }));
    }, [openThread, threads]);

    const submitComment = useCallback(async (row) => {
        const text = (drafts[row.id] || '').trim();
        if (!text || !userId || posting) return;
        setPosting(true);
        const saved = await postMilestoneComment(row.id, userId, displayName, text, isAdmin);
        setPosting(false);
        if (!saved) return;
        setThreads((prev) => ({ ...prev, [row.id]: [...(prev[row.id] || []), saved] }));
        setDrafts((prev) => ({ ...prev, [row.id]: '' }));
        setRows((prev) => prev.map((r) => (
            r.id === row.id ? { ...r, commentCount: (r.commentCount || 0) + 1 } : r
        )));
        // Tell the milestone's owner someone cheered them on. Fire-and-forget:
        // the comment is already saved and visible, and this must never block or
        // fail the post. Self-comments are skipped server-side.
        if (row.userId !== userId) {
            notifyMilestoneComment(
                { milestoneId: row.id, commentId: saved.id, commentText: text, fromName: displayName },
                getToken,
            );
        }
    }, [drafts, userId, displayName, isAdmin, posting, getToken]);

    const removeComment = useCallback(async (row, c) => {
        const ok = await deleteMilestoneComment(c.id, row.id, userId, isAdmin);
        if (!ok) return;
        setThreads((prev) => ({ ...prev, [row.id]: (prev[row.id] || []).filter((x) => x.id !== c.id) }));
        setRows((prev) => prev.map((r) => (
            r.id === row.id ? { ...r, commentCount: Math.max(0, (r.commentCount || 0) - 1) } : r
        )));
    }, [userId, isAdmin]);

    if (loading) {
        return (
            <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <div className="mom-spinner" style={{
                    width: 26, height: 26, margin: '0 auto',
                    border: '2.5px solid var(--mom-cream-dark)',
                    borderTopColor: 'var(--mom-primary)', borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                }} />
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <p style={{
                fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)',
                textAlign: 'center', padding: '32px 12px', lineHeight: 1.7,
            }}>
                {emptyLabel || 'The first milestones will appear here soon.'}
            </p>
        );
    }

    return (
        <div>
            {rows.map((row) => {
                const isOpen = openThread === row.id;
                const thread = threads[row.id] || [];
                return (
                    <div key={row.id} style={{
                        display: 'flex', gap: 16, padding: '20px 0',
                        borderBottom: '1px solid var(--mom-cream-dark)',
                    }}>
                        <div style={{ flex: '0 0 auto' }}>
                            {BADGE_BY_KEY[row.badgeKey] ? (
                                <button
                                    type="button"
                                    aria-label={`${row.badgeName || BADGE_BY_KEY[row.badgeKey].name} — how it's earned`}
                                    onClick={() => setDetail({ badgeKey: row.badgeKey, name: row.displayName, earnedAt: row.earnedAt, own: !!userId && row.userId === userId })}
                                    style={{ ...badgeButtonReset, display: 'block', borderRadius: '50%' }}
                                >
                                    <MilestoneBadge badgeKey={row.badgeKey} size={56} />
                                </button>
                            ) : (
                                <MilestoneBadge badgeKey={row.badgeKey} size={56} />
                            )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 14.5,
                                color: 'var(--mom-charcoal)', lineHeight: 1.5,
                            }}>
                                <MemberCardTrigger userId={row.userId} name={row.displayName} display="inline">
                                {photoFor(row.userId) && (
                                    <span style={{
                                        display: 'inline-block', position: 'relative', width: 20, height: 20,
                                        borderRadius: '50%', verticalAlign: '-4px', marginRight: 6,
                                        background: 'color-mix(in srgb, var(--mom-charcoal) 10%, var(--mom-cream))',
                                    }}><AvatarPhoto url={photoFor(row.userId)} /></span>
                                )}
                                <b style={{ fontWeight: 700 }}>{row.displayName}</b>
                                </MemberCardTrigger>
                                {' earned '}
                                <b style={{ fontWeight: 700 }}>{row.badgeName || row.badgeKey}</b>
                            </div>
                            <div style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 11.5,
                                color: 'var(--mom-warm-gray)', marginTop: 3,
                            }}>
                                {relativeTime(row.earnedAt)}
                            </div>

                            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                <CheerButton
                                    on={cheered.has(row.id)}
                                    count={row.cheerCount || 0}
                                    onClick={() => onCheer(row)}
                                    disabled={!userId}
                                />
                                <button
                                    onClick={() => openComments(row)}
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer', padding: '7px 6px',
                                        fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, fontWeight: 600,
                                        color: 'var(--mom-warm-gray)',
                                    }}
                                >
                                    {row.commentCount > 0
                                        ? `${row.commentCount} comment${row.commentCount === 1 ? '' : 's'}`
                                        : 'Say something'}
                                </button>
                            </div>

                            {isOpen && (() => {
                                const visible = commentShown[row.id] || COMMENTS_VISIBLE;
                                const hidden = Math.max(0, thread.length - visible);
                                return (
                                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {hidden > 0 && (
                                        <button
                                            onClick={() => setCommentShown((prev) => ({
                                                ...prev,
                                                [row.id]: (prev[row.id] || COMMENTS_VISIBLE) + COMMENTS_STEP,
                                            }))}
                                            style={{
                                                alignSelf: 'flex-start', background: 'none', border: 'none',
                                                padding: '2px 0', cursor: 'pointer',
                                                fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                                                color: 'var(--mom-link, var(--mom-primary))',
                                            }}
                                        >
                                            Show earlier comments ({hidden})
                                        </button>
                                    )}
                                    {thread.slice(-visible).map((c) => (
                                        <CommentRow
                                            key={c.id}
                                            c={c}
                                            canDelete={!!userId && (isAdmin || c.userId === userId)}
                                            onDelete={() => removeComment(row, c)}
                                            photoUrl={photoFor(c.userId)}
                                        />
                                    ))}
                                    {userId && (
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                            <input
                                                className="mom-input"
                                                value={drafts[row.id] || ''}
                                                maxLength={280}
                                                placeholder="Add a word of encouragement…"
                                                onChange={(e) => setDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                                                onKeyDown={(e) => { if (e.key === 'Enter') submitComment(row); }}
                                                style={{
                                                    flex: 1, fontSize: 13, padding: '10px 14px', borderRadius: 12,
                                                    fontFamily: 'var(--mom-font-sans)',
                                                }}
                                            />
                                            <button
                                                onClick={() => submitComment(row)}
                                                disabled={posting || !(drafts[row.id] || '').trim()}
                                                className="mom-btn mom-btn-primary"
                                                style={{
                                                    padding: '10px 16px', borderRadius: 12, fontSize: 13, fontWeight: 700,
                                                    fontFamily: 'var(--mom-font-sans)',
                                                    opacity: posting || !(drafts[row.id] || '').trim() ? 0.5 : 1,
                                                }}
                                            >
                                                Post
                                            </button>
                                        </div>
                                    )}
                                </div>
                                );
                            })()}
                        </div>
                    </div>
                );
            })}

            {hasMore && (
                <div style={{ textAlign: 'center', paddingTop: 18 }}>
                    <button
                        onClick={loadMore}
                        disabled={loadingMore}
                        className="mom-btn"
                        style={{
                            padding: '10px 22px', borderRadius: 99,
                            fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                            background: 'var(--mom-cream)', color: 'var(--mom-charcoal)', border: 'none',
                            opacity: loadingMore ? 0.6 : 1,
                        }}
                    >
                        {loadingMore ? 'Loading…' : 'Show more'}
                    </button>
                </div>
            )}

            {detail && (
                <MilestoneDetail
                    badgeKey={detail.badgeKey}
                    keys={[detail.badgeKey]}
                    milestones={viewerMilestones}
                    copy={copy}
                    earnedBy={detail.own && viewerMilestones ? null : { name: detail.name, earnedAt: detail.earnedAt }}
                    onClose={() => setDetail(null)}
                />
            )}
        </div>
    );
}

export default MilestoneWall;
